// src/components/finance/PartnerLockup.jsx
//
// "Powered by …" credit for a content partner. Same discipline as the EAP
// provider lockup: renders the logo when there is one and it loads, and the
// partner's names as text otherwise — never a broken image tile on a page
// that carries a bank's name.
//
// `onDark` flips the palette for the brand-gradient hero; the default sits on
// a light card or footer.

import React, { useState } from 'react';

const PartnerLockup = ({ partner, onDark = false, compact = false, className = '' }) => {
    const [failed, setFailed] = useState({});
    if (!partner) return null;

    // Each mark fails independently; the text fallback only appears when the
    // primary mark is gone, since the names would duplicate a visible wordmark.
    const marks = [
        { key: 'primary', src: partner.logoUrl, alt: partner.name },
        { key: 'co', src: partner.coLogoUrl, alt: partner.coName }
    ].filter((m) => m.src && !failed[m.key]);
    const showLogo = marks.some((m) => m.key === 'primary');

    const eyebrow = onDark ? 'text-white/60' : 'text-slate-400';
    const name = onDark ? 'text-white' : 'text-slate-900';
    const sub = onDark ? 'text-white/70' : 'text-slate-500';
    const tile = `flex items-center justify-center rounded-lg px-2.5 ${compact ? 'h-8' : 'h-10'} ${onDark ? 'bg-white' : 'bg-white border border-slate-200'}`;
    // Both marks are very wide wordmarks (6:1 and 11:1); the width cap, not the
    // height, is what sizes them. Kept deliberately modest — this is a credit,
    // not a headline.
    const img = `${compact ? 'max-h-4 max-w-[110px]' : 'max-h-5 max-w-[140px]'} object-contain`;

    const body = (
        <span className={`inline-flex flex-col gap-1.5 ${className}`}>
            <span className={`text-[10px] font-black uppercase tracking-widest ${eyebrow}`}>
                {partner.poweredBy || `Powered by ${partner.shortName || partner.name}`}
            </span>
            {marks.length > 0 ? (
                <span className="flex flex-wrap items-center gap-2">
                    {marks.map((m) => (
                        <span key={m.key} className={tile}>
                            <img
                                src={m.src}
                                alt={m.alt}
                                onError={() => setFailed((f) => ({ ...f, [m.key]: true }))}
                                className={img}
                            />
                        </span>
                    ))}
                </span>
            ) : null}
            {!showLogo && (
                <span className={`${compact ? 'text-sm' : 'text-base'} font-black leading-tight ${name}`}>
                    {partner.name}
                    {partner.coName && <span className={`font-bold ${sub}`}> · {partner.coName}</span>}
                </span>
            )}
        </span>
    );

    if (partner.url) {
        return (
            <a href={partner.url} target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-90 transition-opacity">
                {body}
            </a>
        );
    }
    return body;
};

export default PartnerLockup;
